import { z } from "zod";
import type { VisitSummary } from './tracking';

export const proposalStatuses = ["draft", "sent", "viewed", "accepted", "paid", "cold", "archived"] as const;
export const blockTypes = ["overview", "cards", "list", "timeline", "investment", "text", "cta"] as const;
export const paymentSchedules = ["full_upfront", "half_upfront", "milestones", "custom"] as const;

export const ProposalStatusSchema = z.enum(proposalStatuses);
export type ProposalStatus = z.infer<typeof ProposalStatusSchema>;
export const PaymentScheduleSchema = z.enum(paymentSchedules);
export type PaymentSchedule = z.infer<typeof PaymentScheduleSchema>;

// Monetary values are integer pence; option setup fees are due upfront.
const MoneySchema = z.number().int().min(0).max(100_000_000);
export const PricingOptionSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(120),
  description: z.string().max(1200),
  amount: MoneySchema,
});
export const ProposalPricingSchema = z.object({
  label: z.string().min(1).max(120),
  baseAmount: MoneySchema,
  baseDueNow: MoneySchema,
  options: z.array(PricingOptionSchema).min(1).max(12),
}).refine((pricing) => pricing.baseDueNow <= pricing.baseAmount, "The starting payment cannot exceed the base fee.")
  .refine((pricing) => new Set(pricing.options.map((option) => option.id)).size === pricing.options.length, "Options need unique IDs.");
export type ProposalPricing = z.infer<typeof ProposalPricingSchema>;
export interface AcceptedQuote {
  optionId: string;
  optionLabel: string;
  optionDescription: string;
  baseAmount: number;
  additionalAmount: number;
  total: number;
  dueNow: number;
}

export function formatMoney(pence: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: pence % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(pence / 100);
}

export function calculateQuote(pricing: ProposalPricing, optionId: string): AcceptedQuote | null {
  const option = pricing.options.find((entry) => entry.id === optionId);
  if (!option) return null;
  return { optionId: option.id, optionLabel: option.label, optionDescription: option.description, baseAmount: pricing.baseAmount, additionalAmount: option.amount, total: pricing.baseAmount + option.amount, dueNow: pricing.baseDueNow + option.amount };
}

export const ProposalItemSchema = z.object({
  id: z.string().min(1).max(80),
  title: z.string().max(180),
  body: z.string().max(3000),
  meta: z.string().max(120),
  url: z.union([z.literal(""), z.string().url().max(1200).refine((value) => /^https?:\/\//i.test(value), "Use an HTTP or HTTPS link.")]).optional(),
  linkLabel: z.string().max(80).optional(),
  relevance: z.string().max(800).optional(),
  imageUrl: z.string().max(1200).refine((value) => !value || /^\/(?!\/)/.test(value) || /^https?:\/\//i.test(value), "Use a local image path or HTTP/HTTPS URL.").optional(),
  imageAlt: z.string().max(240).optional(),
});

export const ProposalSectionSchema = z.object({
  id: z.string().min(1).max(80),
  navLabel: z.string().min(1).max(60),
  eyebrow: z.string().max(80),
  title: z.string().min(1).max(220),
  body: z.string().max(6000),
  type: z.enum(blockTypes),
  items: z.array(ProposalItemSchema).max(24),
  pricing: ProposalPricingSchema.optional(),
}).refine((section) => !section.pricing || section.type === "investment", "Selectable pricing belongs in an Investment section.");

export type ProposalItem = z.infer<typeof ProposalItemSchema>;
export type ProposalSection = z.infer<typeof ProposalSectionSchema>;

export const ProposalUpdateSchema = z.object({
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).min(2).max(80),
  clientName: z.string().min(1).max(120),
  clientEmail: z.string().max(180),
  title: z.string().min(1).max(180),
  projectLabel: z.string().max(100),
  intro: z.string().max(4000),
  price: z.string().max(80),
  paymentSchedule: PaymentScheduleSchema,
  amountDueToBegin: z.string().max(80),
  paymentMethod: z.string().max(120),
  paymentLink: z.union([z.literal(""), z.string().url().max(1200)]),
  paymentNotes: z.string().max(3000),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  heroImage: z.union([z.literal(""), z.string().url().max(1200)]),
  status: ProposalStatusSchema,
  published: z.boolean(),
  isPrivate: z.boolean(),
  accessCode: z.string().max(120).optional(),
  clearAccessCode: z.boolean().optional(),
  sections: z.array(ProposalSectionSchema).min(1).max(30).refine((sections) => sections.filter((section) => section.pricing).length <= 1, "Use one section for selectable pricing."),
});

export type ProposalUpdate = z.infer<typeof ProposalUpdateSchema>;

export interface Proposal extends Omit<ProposalUpdate, "accessCode" | "clearAccessCode"> {
  id: string;
  hasAccessCode: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  viewedAt: string | null;
  acceptedAt: string | null;
  paidAt: string | null;
  coldAt: string | null;
  acceptedByName: string | null;
  acceptedByEmail: string | null;
  acceptanceNote: string | null;
  acceptedQuote: AcceptedQuote | null;
}

export interface ProposalEvent {
  id: number;
  eventType: string;
  detail: string;
  createdAt: string;
}

export interface ProposalDetail {
  proposal: Proposal;
  // Only returned by authenticated editor endpoints, never public proposal data.
  accessCode: string | null;
  events: ProposalEvent[];
}

export interface DashboardData {
  proposals: Proposal[];
  counts: Record<ProposalStatus, number>;
  tracking: Record<string, VisitSummary>;
}

export interface BusinessSettings {
  businessName: string;
  businessEmail: string;
  businessPhone: string;
  baseUrl: string;
}

export interface PublicProposalData {
  proposal: Proposal;
  settings: BusinessSettings;
}

const item = (title: string, body: string, meta = ""): ProposalItem => ({
  id: crypto.randomUUID(),
  title,
  body,
  meta,
});

export function createDefaultSections(): ProposalSection[] {
  return [
    {
      id: crypto.randomUUID(),
      navLabel: "Overview",
      eyebrow: "Project proposal",
      title: "A clear digital home for your next chapter.",
      body: "A focused proposal built around the outcome you need, the work required to deliver it and a straightforward route to getting started.",
      type: "overview",
      items: [],
    },
    {
      id: crypto.randomUUID(),
      navLabel: "The brief",
      eyebrow: "In short",
      title: "What this project needs to achieve.",
      body: "Summarise the client’s situation in plain language. Keep this centred on their goals, their customers and the result—not the technology underneath it.",
      type: "text",
      items: [],
    },
    {
      id: crypto.randomUUID(),
      navLabel: "Scope",
      eyebrow: "The work",
      title: "A complete, joined-up delivery.",
      body: "Break the work into a few decisive areas so the client can see exactly what is included.",
      type: "cards",
      items: [
        item("Strategy & structure", "Planning, content hierarchy and the practical decisions needed before production begins.", "01"),
        item("Design & build", "A polished, accessible experience designed around the client’s brand and audience.", "02"),
        item("Launch & handover", "Testing, launch preparation, training and a clean handover into ongoing support.", "03"),
      ],
    },
    {
      id: crypto.randomUUID(),
      navLabel: "Deliverables",
      eyebrow: "Included",
      title: "Everything required for a confident launch.",
      body: "Use this section for the concrete deliverables included in the fee.",
      type: "list",
      items: [
        item("Discovery and planning", "A focused working session to settle scope, priorities and responsibilities."),
        item("Responsive delivery", "A carefully tested experience across phones, tablets and desktop screens."),
        item("Training and documentation", "Practical support so the client knows how to use what has been built."),
      ],
    },
    {
      id: crypto.randomUUID(),
      navLabel: "Timeline",
      eyebrow: "Delivery",
      title: "A simple route from approval to launch.",
      body: "Timing begins once the proposal is approved and the essential information is available.",
      type: "timeline",
      items: [
        item("Planning", "Confirm content, access, responsibilities and the delivery schedule.", "Start"),
        item("First draft", "Share a working version for focused review and feedback.", "Phase 01"),
        item("Launch", "Apply agreed feedback, test the final result and move into production.", "Phase 02"),
      ],
    },
    {
      id: crypto.randomUUID(),
      navLabel: "Investment",
      eyebrow: "Investment",
      title: "One clear fee for the complete project.",
      body: "State what the fee covers, when payment is due and any third-party costs the client pays directly.",
      type: "investment",
      items: [],
    },
    {
      id: crypto.randomUUID(),
      navLabel: "Next steps",
      eyebrow: "Ready to begin",
      title: "Approve the proposal and book the start.",
      body: "Accept the proposal below, or contact Leviathan if anything needs to be clarified first.",
      type: "cta",
      items: [],
    },
  ];
}
